import React, { useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import {
  Dialog, DialogTitle, DialogContent, DialogActions,
  Button, TextField, Box, Typography, Alert, CircularProgress,
  ToggleButtonGroup, ToggleButton,
} from '@mui/material';
import CameraAltIcon from '@mui/icons-material/CameraAlt';
import NfcIcon from '@mui/icons-material/Nfc';
import KeyboardIcon from '@mui/icons-material/Keyboard';
import { BrowserMultiFormatReader } from '@zxing/browser';

const HAS_NFC = typeof window !== 'undefined' && 'NDEFReader' in window;

export default function TireScanner({ open, onClose, onCapture }) {
  const { t } = useTranslation('tires');
  const [mode, setMode]       = useState('camera'); // 'camera' | 'nfc' | 'manual'
  const [manual, setManual]   = useState('');
  const [error, setError]     = useState('');
  const [status, setStatus]   = useState('');
  const videoRef              = useRef(null);
  const readerRef             = useRef(null);
  const controlsRef           = useRef(null);
  const nfcAbortRef           = useRef(null);

  // ── Avvio camera ────────────────────────────────────────────
  useEffect(() => {
    if (!open || mode !== 'camera') return;
    let cancelled = false;
    setError('');
    setStatus(t('scanner_camera_prompt'));

    const reader = new BrowserMultiFormatReader();
    readerRef.current = reader;

    (async () => {
      try {
        const controls = await reader.decodeFromVideoDevice(
          undefined,
          videoRef.current,
          (result, err, ctl) => {
            if (cancelled) return;
            if (result) {
              const text = result.getText();
              ctl?.stop();
              onCapture(text);
            }
          },
        );
        controlsRef.current = controls;
      } catch (e) {
        if (!cancelled) {
          setError(t('scanner_camera_error'));
        }
      }
    })();

    return () => {
      cancelled = true;
      try { controlsRef.current?.stop(); } catch (_) {}
    };
  }, [open, mode, onCapture, t]);

  // ── NFC ─────────────────────────────────────────────────────
  useEffect(() => {
    if (!open || mode !== 'nfc' || !HAS_NFC) return;
    setError('');
    setStatus(t('scanner_nfc_prompt'));

    const ndef = new window.NDEFReader();
    const ctrl = new AbortController();
    nfcAbortRef.current = ctrl;

    (async () => {
      try {
        await ndef.scan({ signal: ctrl.signal });
        ndef.addEventListener('reading', ({ serialNumber, message }) => {
          let text = serialNumber || '';
          if (message?.records?.length) {
            const r0 = message.records[0];
            if (r0.recordType === 'text') {
              const td = new TextDecoder(r0.encoding || 'utf-8');
              text = td.decode(r0.data) || text;
            }
          }
          if (text) onCapture(text);
        });
      } catch (e) {
        setError(t('scanner_nfc_error', { message: e.message }));
      }
    })();

    return () => { try { ctrl.abort(); } catch (_) {} };
  }, [open, mode, onCapture, t]);

  // ── Reset al chiudere ─────────────────────────────────────
  const handleClose = () => {
    try { controlsRef.current?.stop(); } catch (_) {}
    try { nfcAbortRef.current?.abort(); } catch (_) {}
    setManual('');
    setError('');
    setStatus('');
    onClose();
  };

  const submitManual = () => {
    const v = manual.trim();
    if (!v) { setError(t('scanner_manual_required')); return; }
    onCapture(v);
  };

  return (
    <Dialog open={open} onClose={handleClose} maxWidth="xs" fullWidth>
      <DialogTitle>{t('scanner_title')}</DialogTitle>
      <DialogContent>
        <ToggleButtonGroup
          value={mode}
          exclusive
          onChange={(_, v) => v && setMode(v)}
          fullWidth
          size="small"
          sx={{ mb: 2 }}
        >
          <ToggleButton value="camera"><CameraAltIcon sx={{ mr: 0.5, fontSize: 18 }} />{t('scanner_mode_camera')}</ToggleButton>
          {HAS_NFC && (
            <ToggleButton value="nfc"><NfcIcon sx={{ mr: 0.5, fontSize: 18 }} />{t('scanner_mode_nfc')}</ToggleButton>
          )}
          <ToggleButton value="manual"><KeyboardIcon sx={{ mr: 0.5, fontSize: 18 }} />{t('scanner_mode_manual')}</ToggleButton>
        </ToggleButtonGroup>

        {error && <Alert severity="warning" sx={{ mb: 2 }}>{error}</Alert>}

        {mode === 'camera' && (
          <Box sx={{ position: 'relative', borderRadius: 1, overflow: 'hidden', backgroundColor: '#000', height: 260 }}>
            <video ref={videoRef} style={{ width: '100%', height: '100%', objectFit: 'cover' }} muted playsInline />
            {!controlsRef.current && (
              <Box sx={{ position: 'absolute', inset: 0, display: 'flex', alignItems: 'center', justifyContent: 'center', color: '#fff' }}>
                <CircularProgress size={28} sx={{ color: '#fff' }} />
              </Box>
            )}
            {status && (
              <Typography variant="caption" sx={{ position: 'absolute', bottom: 8, left: 12, color: '#fff', textShadow: '0 1px 2px rgba(0,0,0,.7)' }}>
                {status}
              </Typography>
            )}
          </Box>
        )}

        {mode === 'nfc' && (
          <Box sx={{ p: 4, textAlign: 'center', border: '2px dashed', borderColor: 'divider', borderRadius: 1 }}>
            <NfcIcon sx={{ fontSize: 56, color: 'text.secondary', mb: 1 }} />
            <Typography variant="body2" color="text.secondary">{status || t('scanner_nfc_waiting')}</Typography>
          </Box>
        )}

        {mode === 'manual' && (
          <Box sx={{ pt: 1 }}>
            <TextField
              fullWidth
              label={t('scanner_manual_label')}
              value={manual}
              onChange={e => setManual(e.target.value)}
              autoFocus
              onKeyDown={e => e.key === 'Enter' && submitManual()}
              placeholder={t('scanner_manual_placeholder')}
            />
          </Box>
        )}
      </DialogContent>
      <DialogActions>
        <Button onClick={handleClose}>{t('cancel')}</Button>
        {mode === 'manual' && (
          <Button onClick={submitManual} variant="contained" disabled={!manual.trim()}>{t('confirm')}</Button>
        )}
      </DialogActions>
    </Dialog>
  );
}
